import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, InputText, SectionCard, TextArea, PageWrapper } from "@/deps/ui-library";
import { api } from "@/api";
import { useAccounts, useSavePurchaseOrder } from "@/api/queries";
import type { Party, PurchaseOrder } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { documentLineAccounts } from "@/lib/accountPickers";
import { LineEditor, type EditableLine } from "@/components/LineEditor/LineEditor";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { usePartyFromAddress } from "@/features/parties/usePartyFromAddress";
import { calculateTotals } from "@/lib/invoiceTotals";
import { DateField } from "@/components/DateField";

const today = () => new Date().toISOString().slice(0, 10);

/** Two weeks on, for someone to change. */
const fortnightFrom = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + 14);
  return day.toISOString().slice(0, 10);
};

const STARTING_TERMS = "Quote the order number on the delivery note and the invoice.";

/**
 * Writes a purchase order (ADR-0055). It saves a draft, and sending it is a step on the order itself.
 * Withholding isn't on an order: it's applied when the bill is paid.
 *
 * Given a draft and its supplier, it edits that draft.
 */
export function PurchaseOrderEditorPage({
  editing,
}: { editing?: { order: PurchaseOrder; supplier: Party } } = {}) {
  const say = useTaxWords();
  const navigate = useNavigate();
  const { data: accounts } = useAccounts();
  // Expenses first, so a new line starts on one, as on a bill.
  // Active ones, never a required account: control, tax and the rest Rigo posts itself (ADR-0066).
  const offered = documentLineAccounts(accounts);
  const expenseAccounts = [
    ...offered.filter((account) => account.group === "Expenses"),
    ...offered.filter((account) => account.group === "Assets"),
  ];
  const save = useSavePurchaseOrder();

  const draft = editing?.order;
  const back = draft
    ? ({ to: "/purchases/orders/$id", params: { id: draft.id } } as const)
    : ({ to: "/purchases/orders" } as const);

  const [supplier, setSupplier] = useState<Party | null>(editing?.supplier ?? null);
  usePartyFromAddress("supplier", setSupplier);
  const [date, setDate] = useState(draft?.date ?? today());
  const [typedExpected, setTypedExpected] = useState<string | null>(draft?.expected ?? null);
  const expected = typedExpected ?? (date ? fortnightFrom(date) : "");
  const [reference, setReference] = useState(draft?.reference ?? "");
  const [deliverTo, setDeliverTo] = useState(draft?.deliverTo ?? "");
  const [terms, setTerms] = useState(draft ? (draft.terms ?? "") : STARTING_TERMS);
  const [note, setNote] = useState(draft?.note ?? "");
  const [lines, setLines] = useState<EditableLine[]>(
    draft
      ? draft.lines.map((line) => ({
          id: String(line.n),
          description: line.description,
          accountId: line.accountId,
          category: Number(line.vatRatePct) > 0 ? "S" : "Z",
          rate: line.vatRatePct,
          grossPrice: line.unitPrice,
          quantity: line.quantity,
        }))
      : [
          {
            id: "1",
            description: "",
            accountId: "",
            category: "S",
            rate: "13",
            grossPrice: "",
            quantity: "1",
          },
        ],
  );
  const withAccounts = lines.map((line) =>
    line.accountId === "" ? { ...line, accountId: expenseAccounts[0]?.id ?? "" } : line,
  );
  const totals = calculateTotals(withAccounts);

  const datesWrong = date !== "" && expected !== "" && expected < date;
  const canSave =
    supplier !== null &&
    date !== "" &&
    expected !== "" &&
    !datesWrong &&
    Number(totals.totalWithVat) > 0 &&
    withAccounts.every(
      (line) =>
        line.accountId !== "" && line.description.trim() !== "" && Number(line.quantity) > 0,
    ) &&
    !save.isPending;

  function submit() {
    save.mutate(
      {
        id: draft?.id,
        supplierId: supplier!.id,
        date,
        expected,
        reference: reference.trim() || undefined,
        deliverTo: deliverTo.trim() || undefined,
        terms: terms.trim() || undefined,
        note: note.trim() || undefined,
        lines: withAccounts.map((line) => ({
          description: line.description,
          accountId: line.accountId,
          quantity: line.quantity,
          unitPrice: line.grossPrice,
          vatRatePct: line.category === "S" ? (line.rate ?? "0") : "0",
          capital: accounts?.find((account) => account.id === line.accountId)?.group === "Assets",
        })),
      },
      {
        onSuccess: (saved) =>
          void navigate({ to: "/purchases/orders/$id", params: { id: saved.id } }),
      },
    );
  }

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title={draft ? "Edit purchase order" : "New purchase order"}
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => void navigate(back)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSave}
              onClick={submit}
            >
              {save.isPending ? "Saving" : "Save draft"}
            </Button>
          </span>
        }
      />

      {save.isError ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={
            save.error instanceof Error
              ? save.error.message
              : "That purchase order could not be saved."
          }
        />
      ) : null}

      <SectionCard title="Purchase order">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <PartyCombobox
            label="Supplier"
            required
            value={supplier}
            onChange={setSupplier}
            search={(q) => api.parties.search("supplier", q)}
          />
          <InputText
            label="Reference"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
          <DateField label="Order date" required value={date} onChange={setDate} />
          <DateField
            label="Expected delivery"
            required
            value={expected}
            onChange={setTypedExpected}
            helperText={datesWrong ? "This is before the order date." : undefined}
          />
        </div>
      </SectionCard>

      <SectionCard title="Lines">
        <LineEditor lines={withAccounts} onChange={setLines} accounts={expenseAccounts} />
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-2">
        <SectionCard title="Delivery and terms">
          <div className="flex flex-col gap-3">
            <TextArea
              label="Deliver to"
              rows={2}
              value={deliverTo}
              onChange={(event) => setDeliverTo(event.target.value)}
            />
            <TextArea
              label="Terms"
              rows={3}
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
            />
            <TextArea
              label="Note"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </SectionCard>
        <div className="flex flex-col gap-4">
          <SectionCard title={say("VAT breakdown")} flush>
            <VatBreakdownTable totals={totals} />
          </SectionCard>
          <SectionCard title="Totals">
            <TotalsBlock totals={totals} showDue={false} />
          </SectionCard>
        </div>
      </div>
    </PageWrapper>
  );
}
